// Chemin : C:\gestion-copro\src\context\ChatContext.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Session 23 : support Agent SENTINELLE avec actions proposées
// Session 95 : Ajout suggestions dynamiques dans le state
// Session 96 : Chargement automatique des suggestions à l'ouverture du chat
// Session 97 : Onboarding SENTINELLE + Persistance conversation Firestore
// Session 97 fix : Correction boucle infinie — ajout flag conversationLoaded

import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { 
  ChatState, 
  ChatAction, 
  ChatMessage, 
  ChatRole, 
  PendingDocument,
  ProposedAction 
} from '../types/chat.types';
import { askAssistant, AssistantResult, fetchSuggestions } from '../services/assistant.service';
import { deleteTempFile } from '../services/documentAnalysis.service';
import { 
  getActiveConversation, 
  createConversation, 
  updateConversationMessages, 
  deleteConversation,
  convertFirestoreMessages 
} from '../services/conversation.service';
import { useAuth } from './AuthContext';
import { useCopropriete } from './CoproprieteContext';

// État initial
const initialState: ChatState = {
  messages: [],
  isLoading: false,
  error: null,
  isOpen: false,
  pendingDocument: null,
  selectedAction: null,
  suggestions: [],
  suggestionsLoading: false,
  onboardingDone: true,
  onboardingLoading: true,
  conversationId: null,
  conversationLoading: false,
  conversationLoaded: false,      // false = pas encore tenté de charger
};

// Reducer
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'OPEN_CHAT':
      return { ...state, isOpen: true };
    case 'CLOSE_CHAT':
      return { ...state, isOpen: false };
    case 'TOGGLE_CHAT':
      return { ...state, isOpen: !state.isOpen };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_HISTORY':
      return { ...state, messages: [], error: null, selectedAction: null, conversationId: null, conversationLoaded: false };
    case 'SET_PENDING_DOCUMENT':
      return { ...state, pendingDocument: action.payload };
    case 'CLEAR_PENDING_DOCUMENT':
      return { ...state, pendingDocument: null };
    case 'SELECT_ACTION':
      return { ...state, selectedAction: action.payload };
    case 'CLEAR_SELECTED_ACTION':
      return { ...state, selectedAction: null };
    case 'MARK_ACTION_EXECUTED':
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.id === action.payload.messageId && msg.actions) {
            return {
              ...msg,
              actions: msg.actions.filter(a => a.id !== action.payload.actionId)
            };
          }
          return msg;
        }),
        selectedAction: null
      };
    case 'SET_SUGGESTIONS':
      return { ...state, suggestions: action.payload };
    case 'SET_SUGGESTIONS_LOADING':
      return { ...state, suggestionsLoading: action.payload };
    case 'SET_ONBOARDING_DONE':
      return { ...state, onboardingDone: action.payload };
    case 'SET_ONBOARDING_LOADING':
      return { ...state, onboardingLoading: action.payload };
    case 'SET_CONVERSATION_ID':
      return { ...state, conversationId: action.payload };
    case 'SET_CONVERSATION_LOADING':
      return { ...state, conversationLoading: action.payload };
    case 'SET_CONVERSATION_LOADED':
      return { ...state, conversationLoaded: action.payload };
    case 'LOAD_CONVERSATION':
      return { 
        ...state, 
        conversationId: action.payload.conversationId, 
        messages: action.payload.messages,
        conversationLoading: false,
        conversationLoaded: true,
      };
    default:
      return state;
  }
}

function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

interface ChatContextType {
  state: ChatState;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (content: string) => Promise<void>;
  addUserMessage: (content: string) => void;
  addAssistantMessage: (content: string, isDocumentAnalysis?: boolean, actions?: ProposedAction[], incitationPieces?: string) => string;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearHistory: () => void;
  setPendingDocument: (doc: PendingDocument | null) => void;
  clearPendingDocument: () => Promise<void>;
  selectAction: (action: ProposedAction) => void;
  clearSelectedAction: () => void;
  markActionExecuted: (messageId: string, actionId: string) => void;
  loadSuggestions: () => Promise<void>;
  loadOnboardingStatus: () => Promise<void>;
  markOnboardingDone: () => Promise<void>;
  loadActiveConversation: () => Promise<void>;
  startNewConversation: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { userProfile } = useAuth();
  const { coproprieteId: activeCoproId } = useCopropriete();
  const savingRef = useRef(false);

  const openChat = useCallback(() => {
    dispatch({ type: 'OPEN_CHAT' });
  }, []);

  const closeChat = useCallback(() => {
    dispatch({ type: 'CLOSE_CHAT' });
  }, []);

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_CHAT' });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const addUserMessage = useCallback((content: string) => {
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
  }, []);

  const addAssistantMessage = useCallback((
    content: string, 
    isDocumentAnalysis?: boolean,
    actions?: ProposedAction[],
    incitationPieces?: string
  ): string => {
    const messageId = generateMessageId();
    const assistantMessage: ChatMessage = {
      id: messageId,
      role: 'assistant',
      content: content,
      timestamp: new Date(),
      isDocumentAnalysis: isDocumentAnalysis || false,
      actions: actions,
      incitationPieces: incitationPieces,
    };
    dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });
    return messageId;
  }, []);

  const setPendingDocument = useCallback((doc: PendingDocument | null) => {
    dispatch({ type: 'SET_PENDING_DOCUMENT', payload: doc });
  }, []);

  const clearPendingDocument = useCallback(async () => {
    if (state.pendingDocument?.tempPath) {
      await deleteTempFile(state.pendingDocument.tempPath);
    }
    dispatch({ type: 'CLEAR_PENDING_DOCUMENT' });
  }, [state.pendingDocument?.tempPath]);

  const selectAction = useCallback((action: ProposedAction) => {
    dispatch({ type: 'SELECT_ACTION', payload: action });
  }, []);

  const clearSelectedAction = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_ACTION' });
  }, []);

  const markActionExecuted = useCallback((messageId: string, actionId: string) => {
    dispatch({ type: 'MARK_ACTION_EXECUTED', payload: { messageId, actionId } });
  }, []);

  // ============================================
  // ONBOARDING SENTINELLE
  // ============================================
  
  const loadOnboardingStatus = useCallback(async () => {
    if (!userProfile?.id) return;

    dispatch({ type: 'SET_ONBOARDING_LOADING', payload: true });

    try {
      const userDoc = await getDoc(doc(db, 'users', userProfile.id));
      const data = userDoc.data();
      const done = data?.sentinelleOnboardingDone === true;
      dispatch({ type: 'SET_ONBOARDING_DONE', payload: done });
    } catch (error) {
      console.error('Erreur chargement onboarding SENTINELLE:', error);
      dispatch({ type: 'SET_ONBOARDING_DONE', payload: true });
    } finally {
      dispatch({ type: 'SET_ONBOARDING_LOADING', payload: false });
    }
  }, [userProfile?.id]);

  const markOnboardingDone = useCallback(async () => {
    if (!userProfile?.id) return;

    dispatch({ type: 'SET_ONBOARDING_DONE', payload: true });

    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        sentinelleOnboardingDone: true,
      });
    } catch (error) {
      console.error('Erreur marquage onboarding SENTINELLE:', error);
    }
  }, [userProfile?.id]);

  // ============================================
  // PERSISTANCE CONVERSATION
  // ============================================

  const saveConversation = useCallback(async (messages: ChatMessage[], currentConversationId: string | null) => {
    const coproprieteId = activeCoproId;
    const userId = userProfile?.id;
    if (!coproprieteId || !userId || messages.length === 0) return;
    if (savingRef.current) return;

    savingRef.current = true;
    try {
      if (currentConversationId) {
        await updateConversationMessages(coproprieteId, currentConversationId, messages);
      } else {
        const newId = await createConversation(coproprieteId, userId, messages);
        dispatch({ type: 'SET_CONVERSATION_ID', payload: newId });
      }
    } catch (error) {
      console.error('Erreur sauvegarde conversation:', error);
    } finally {
      savingRef.current = false;
    }
  }, [activeCoproId, userProfile?.id]);

  // Charger la dernière conversation active — UNE SEULE tentative
  const loadActiveConversation = useCallback(async () => {
    const coproprieteId = activeCoproId;
    const userId = userProfile?.id;
    if (!coproprieteId || !userId) return;

    dispatch({ type: 'SET_CONVERSATION_LOADING', payload: true });

    try {
      const conv = await getActiveConversation(coproprieteId, userId);
      
      if (conv && conv.messages && conv.messages.length > 0) {
        const messages = convertFirestoreMessages(conv.messages);
        dispatch({ type: 'LOAD_CONVERSATION', payload: { conversationId: conv.id, messages } });
      } else {
        // Aucune conversation trouvée — marquer comme chargé quand même
        dispatch({ type: 'SET_CONVERSATION_LOADED', payload: true });
        dispatch({ type: 'SET_CONVERSATION_LOADING', payload: false });
      }
    } catch (error) {
      console.error('Erreur chargement conversation active:', error);
      // IMPORTANT : marquer comme chargé même en cas d'erreur → empêche la boucle infinie
      dispatch({ type: 'SET_CONVERSATION_LOADED', payload: true });
      dispatch({ type: 'SET_CONVERSATION_LOADING', payload: false });
    }
  }, [activeCoproId, userProfile?.id]);

  const startNewConversation = useCallback(async () => {
    const coproprieteId = activeCoproId;

    if (coproprieteId && state.conversationId) {
      try {
        await deleteConversation(coproprieteId, state.conversationId);
      } catch (error) {
        console.error('Erreur suppression conversation:', error);
      }
    }

    dispatch({ type: 'CLEAR_HISTORY' });
  }, [activeCoproId, state.conversationId]);

  const clearHistory = useCallback(async () => {
    const coproprieteId = activeCoproId;

    if (coproprieteId && state.conversationId) {
      try {
        await deleteConversation(coproprieteId, state.conversationId);
      } catch (error) {
        console.error('Erreur suppression conversation:', error);
      }
    }

    dispatch({ type: 'CLEAR_HISTORY' });
  }, [activeCoproId, state.conversationId]);

  // ============================================
  // SUGGESTIONS
  // ============================================

  const loadSuggestions = useCallback(async () => {
    const coproprieteId = activeCoproId;
    if (!coproprieteId) return;
    if (state.suggestionsLoading) return;

    dispatch({ type: 'SET_SUGGESTIONS_LOADING', payload: true });

    try {
      const result = await fetchSuggestions(coproprieteId);
      // Session 170 : fetchSuggestions retourne désormais un SuggestionsResult
      const suggestions = result.suggestions || [];
      if (suggestions.length > 0) {
        dispatch({ type: 'SET_SUGGESTIONS', payload: suggestions });
      }
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    } finally {
      dispatch({ type: 'SET_SUGGESTIONS_LOADING', payload: false });
    }
  }, [activeCoproId, state.suggestionsLoading]);

  // ============================================
  // ENVOI DE MESSAGE + SAUVEGARDE AUTO
  // ============================================

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || state.isLoading) return;

    const coproprieteId = activeCoproId;
    if (!coproprieteId) {
      dispatch({ type: 'SET_ERROR', payload: 'Aucune copropriété associée à votre compte' });
      return;
    }

    const userMessage: ChatMessage = {
      id: generateMessageId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const conversationHistory = state.messages.map((msg) => ({
        role: msg.role as ChatRole,
        content: msg.content,
      }));

      const result: AssistantResult = await askAssistant(content.trim(), conversationHistory, coproprieteId);

      const assistantMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
        actions: result.actions,
        incitationPieces: result.incitationPieces,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage });

      if (result.suggestions && result.suggestions.length > 0) {
        dispatch({ type: 'SET_SUGGESTIONS', payload: result.suggestions });
      }

      // Sauvegarde automatique après chaque échange
      const allMessages = [...state.messages, userMessage, assistantMessage];
      await saveConversation(allMessages, state.conversationId);

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.isLoading, state.messages, state.conversationId, activeCoproId, saveConversation]);

  const value: ChatContextType = {
    state,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    addUserMessage,
    addAssistantMessage,
    setLoading,
    setError,
    clearHistory,
    setPendingDocument,
    clearPendingDocument,
    selectAction,
    clearSelectedAction,
    markActionExecuted,
    loadSuggestions,
    loadOnboardingStatus,
    markOnboardingDone,
    loadActiveConversation,
    startNewConversation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextType {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat doit être utilisé dans un ChatProvider');
  }
  return context;
}
